/**
 * OAuth Families — single source of truth for token-lifecycle config.
 *
 * Replaces the 6 hardcoded provider lists scattered across `apps/server` (cron
 * monitor, queue health-check switch, queue recovery-refresh switch, inline-
 * refresh allowlist, schema enum, plus the bug at widget-populate.ts:547).
 * Adding a new OAuth integration becomes "write one entry here" — never touch
 * the cron, the queue, or the tracker again.
 *
 * Architecture overview (full spec: tech-spec-token-lifecycle-refactor.md):
 *  - Each family declares TTLs, refresh policy, failure thresholds, optional
 *    health-check endpoint, and queue handlers (healthCheck / recoveryRefresh /
 *    optional inlineRefreshHandler).
 *  - Discriminated union forces `inlineRefreshHandler` to be present at the
 *    type level when `inlineRefreshOn401: true` (TD19).
 *  - `as const satisfies Record<OAuthFamilyName, FamilyLifecycleConfig>` gives
 *    compile-time exhaustiveness against `OAUTH_PROVIDER_VALUES` (TD15).
 *  - The runtime tests in oauth-families.test.ts catch the inverse drift
 *    direction (entry exists here but missing from the schema enum).
 *
 * PR-1 scaffolding policy (TD11):
 *  - Every family entry ships in PR-1 with stub `healthCheck` /
 *    `recoveryRefresh` (and `inlineRefreshHandler` when applicable) created
 *    by the `makePr1*Stub` factories. Those throw if invoked, but the
 *    dispatchers in apps/server are NOT yet wired to read from this map in
 *    PR-1 — they still go through the legacy `switch (token.provider)` blocks.
 *    PR-2 replaces every stub with a real handler AND swaps the dispatchers.
 *  - Discovery Item 7: stubs carry a non-enumerable `isPr1Stub: true` sentinel
 *    so the PR-2 runtime-gate test (`isPr1StubHandler`) can distinguish stub
 *    from real handler — `typeof === "function"` alone cannot.
 */
import type { Platform } from "@provenleap/db";
import type {
  AdapterContext,
  AdapterError,
  AdapterErrorCode,
  AdapterResult,
  OAuthCredentials,
  RefreshedTokens,
} from "./adapter";
import { ADAPTER_META } from "./registry";

// ============================================================================
// Family-name type — sourced from the schema enum to keep dep direction sane
// ============================================================================

/**
 * Family-name type. Re-exported from the existing schema enum so the two stay
 * in lockstep without `packages/db` having to import from `packages/integrations`.
 *
 * Adding a value here REQUIRES adding it to OAUTH_PROVIDER_VALUES in
 * packages/db/src/schema/oauth-tokens.ts first (Task 1.1 of the refactor).
 */
export const OAUTH_FAMILY_NAMES = [
  "google",
  "meta",
  "linkedin",
  "tiktok",
  "shopify",
  "mailchimp",
  "hubspot",
  "klaviyo",
  "microsoft-ads",
  "pinterest-ads",
  "snapchat-ads",
  "reddit-ads",
  "amazon-ads",
  "ahrefs",
  "semrush",
  "moz",
  "callrail",
  "calltrackingmetrics",
  "trustpilot",
  "yelp",
] as const;

export type OAuthFamilyName = (typeof OAUTH_FAMILY_NAMES)[number];

// ============================================================================
// Refresh-context type (Discovery Item 5, option a)
// ============================================================================

/**
 * Context passed to adapter `refreshToken()` calls dispatched from the cron-
 * driven queue. `clientId` and `integrationId` are unavailable because the
 * queue dispatch only has a token row — multi-platform OAuth families like
 * Meta have N integrations per token, so no single integrationId applies.
 *
 * Per Discovery Item 5 we widened ONLY the refresh path rather than relaxing
 * AdapterContext (which has 25+ non-optional consumers in adapters' fetch
 * code). The narrower change keeps existing call sites strict.
 *
 * Adapters that read `clientId` / `integrationId` inside `refreshToken()`
 * MUST guard with null checks; current 7 implementations do not depend on
 * them (verified via F10 audit).
 */
export type AdapterRefreshContext = Omit<AdapterContext, "clientId" | "integrationId"> & {
  readonly clientId?: string;
  readonly integrationId?: string;
};

// ============================================================================
// Env-binding key list (TD17 + R2#8 + R3#4)
// ============================================================================

/**
 * Env keys required for OAuth refresh — per-family declarations live in
 * OAUTH_FAMILIES[*].requiredEnvKeys. The dispatcher pre-flight check in PR-2
 * compares these to the runtime env BEFORE invoking the adapter, so a missing
 * binding routes to `deployment_misconfig` instead of producing an ambiguous
 * `token_invalid` error.
 *
 * The runtime test in apps/server/src/queues/token-refresh.test.ts asserts
 * every value here is a valid key on TokenRefreshEnv (catches drift in either
 * direction — adding a key here without adding it to the env type, or vice
 * versa, fails CI).
 */
export const OAUTH_ENV_KEYS = [
  "GOOGLE_CLIENT_ID",
  "GOOGLE_CLIENT_SECRET",
  "META_APP_ID",
  "META_APP_SECRET",
  "LINKEDIN_CLIENT_ID",
  "LINKEDIN_CLIENT_SECRET",
  "TIKTOK_CLIENT_KEY",
  "TIKTOK_CLIENT_SECRET",
  "MICROSOFT_ADS_CLIENT_ID",
  "MICROSOFT_ADS_CLIENT_SECRET",
  "PINTEREST_ADS_CLIENT_ID",
  "PINTEREST_ADS_CLIENT_SECRET",
  "KLAVIYO_CLIENT_ID",
  "KLAVIYO_CLIENT_SECRET",
  "HUBSPOT_CLIENT_ID",
  "HUBSPOT_CLIENT_SECRET",
  "SNAPCHAT_ADS_CLIENT_ID",
  "SNAPCHAT_ADS_CLIENT_SECRET",
  "REDDIT_ADS_CLIENT_ID",
  "REDDIT_ADS_CLIENT_SECRET",
  "AMAZON_ADS_CLIENT_ID",
  "AMAZON_ADS_CLIENT_SECRET",
] as const;

export type OAuthEnvKey = (typeof OAUTH_ENV_KEYS)[number];

// ============================================================================
// Result types for queue handlers
// ============================================================================

/**
 * R2#15: discriminated union so the dispatcher MUST exhaustively handle the
 * "deferred" case (handler already called message.retry, dispatcher must NOT
 * act on the result).
 */
export type HealthCheckResult =
  | {
      readonly kind: "result";
      readonly isHealthy: boolean;
      readonly newAccessTokenEncrypted?: string;
      readonly newAccessTokenExpiresAt?: Date;
    }
  | { readonly kind: "deferred" };

/**
 * Result of an inline-refresh attempt (api-call-tracker on-401 path). The
 * surrounding `attemptInlineRefresh` does D1/KV loading + decryption +
 * encryption + persistence; the per-family handler only owns the token-
 * endpoint POST.
 *
 * Returns null for `invalid_grant` and other "refresh token is broken" cases
 * the caller treats as permanent failure.
 */
export type InlineRefreshResult = {
  readonly newAccessToken: string;
  readonly newAccessTokenEncrypted: string;
  readonly expiresAt: Date;
  readonly rotatedRefreshTokenEncrypted?: string;
} | null;

/**
 * Outcome wrapper exposed by `attemptInlineRefresh` to its caller.
 * F7 / TD16: replaces the original 7-way overloaded `null` return.
 *
 *   - retried: refresh succeeded and the original `fn()` was retried; caller
 *     returns this verbatim.
 *   - lock_contended: another worker is mid-refresh; caller waits 200ms then
 *     retries `fn()` once (TD16 lock-contention fast-retry).
 *   - permanent_failure: invalid_grant or no-refresh-token; caller returns
 *     the original 401 — retrying is doomed.
 *   - transient_failure: fetch timeout / 5xx / bad JSON; caller returns the
 *     original 401 — provider will retry on the next cron tick.
 */
export type InlineRefreshOutcome<T> =
  | { readonly kind: "retried"; readonly result: AdapterResult<T> }
  | { readonly kind: "lock_contended" }
  | { readonly kind: "permanent_failure" }
  | { readonly kind: "transient_failure" };

/** Counters threaded through queue dispatch for observability. */
export type RefreshMetrics = {
  success: number;
  failure: number;
  version_conflicts: number;
};

// ============================================================================
// CronAction — error-mapping target enum (TD12, TD23)
// ============================================================================

/**
 * Five distinct cron-side actions covering all 9 AdapterErrorCode values plus
 * the deployment-misconfig case caught by the dispatcher pre-flight (TD17).
 *
 * The `applyAction` helper in apps/server/src/queues/token-refresh.ts owns
 * the runtime behavior for each variant. This package only defines the union
 * + the error→action mapping.
 */
export type CronAction =
  | "rate_limit_retry"
  | "transient_retry"
  | "mark_revoked_notify_user"
  | "log_and_retry_once"
  | "deployment_misconfig";

/**
 * Map every AdapterErrorCode to its CronAction (TD12, TD22).
 *
 * Exhaustiveness check: adding a new variant to AdapterErrorCode (e.g.
 * `not_implemented` from a future scaffold story) forces `tsc` to error on
 * the `_exhaustive: never` line, surfacing the missing mapping at compile
 * time rather than at the next 401.
 */
export function adapterErrorToCronAction(code: AdapterErrorCode): CronAction {
  switch (code) {
    case "rate_limited":
      return "rate_limit_retry";
    case "provider_unavailable":
      return "transient_retry";
    case "network_error":
      return "transient_retry";
    case "token_expired":
      return "mark_revoked_notify_user";
    case "token_invalid":
      // F8: pre-flight env check (TD17) catches deployment-misconfig BEFORE
      // the adapter runs, so this code reliably means "refresh token broken"
      // by the time we reach this mapping.
      return "mark_revoked_notify_user";
    case "permission_denied":
      return "mark_revoked_notify_user";
    case "api_not_enabled":
      return "mark_revoked_notify_user";
    case "account_not_found":
      return "mark_revoked_notify_user";
    case "invalid_response":
      return "log_and_retry_once";
    case "invalid_request":
      // Story 19.15 — `invalid_request` is a per-call client-side error
      // (e.g. mixed-product widget rejection); it is NOT a token-refresh
      // failure mode. Treat as log_and_retry_once so the queue does not loop.
      return "log_and_retry_once";
    case "brand_registry_required":
      // Story 19.15 — customer-side enrollment gap. Surface to the user; do
      // NOT retry or revoke. The integration may still be `healthy` overall
      // (multi-surface SP+SD work even when SB is gated).
      return "log_and_retry_once";
    case "partial_surface_unavailable":
      // Story 19.15 — one ad-product surface unavailable; the integration is
      // still healthy from at least one other surface. Log and continue.
      return "log_and_retry_once";
    case "dsp_not_enabled":
      // Story 19.16 — DSP is sales-rep-gated on the customer side. Surface
      // to the user; do NOT retry or revoke. The integration stays healthy
      // when SP+SB+SD work but DSP is gated (partial-surface-unavailable
      // pattern carried forward from 19.15's `brand_registry_required`).
      return "log_and_retry_once";
    case "dsp_audience_not_enabled":
    case "dsp_technical_not_provisioned":
      // Story 19.16.1 — per-report-type gating; same partial-surface-
      // unavailable handling as `dsp_not_enabled`.
      return "log_and_retry_once";
    default: {
      // Compile-time exhaustiveness — adding a new AdapterErrorCode without
      // updating this switch is a TS error (TD22).
      const _exhaustive: never = code;
      void _exhaustive;
      // Runtime-safe fallback in case the type and value somehow diverge in
      // production (e.g., a non-typed import path).
      return "transient_retry";
    }
  }
}

// ============================================================================
// FamilyLifecycleConfig (TD2, TD19)
// ============================================================================

/**
 * Generic shape of an env binding container — a record keyed by OAuthEnvKey
 * with optional string values. The actual `TokenRefreshEnv` in apps/server
 * has many more fields; this package only cares about the OAuth-credential
 * subset, so we use a structural type that any superset satisfies.
 */
export type OAuthEnvLike = { readonly [K in OAuthEnvKey]?: string };

/**
 * Queue health-check handler — called by `handleTokenRefreshQueue` for
 * `health_check` messages. May return `kind: "deferred"` to signal "I already
 * called message.retry, dispatcher should not act."
 */
export type HealthCheckHandler<TEnv extends OAuthEnvLike = OAuthEnvLike> = (
  token: unknown, // DecryptedToken — defined in apps/server, opaque here
  env: TEnv,
  message: unknown // Message<TokenRefreshMessage> — defined in apps/server
) => Promise<HealthCheckResult>;

/**
 * Queue recovery-refresh handler — called for `recovery_refresh` messages.
 * Owns full message lifecycle (DB write, KV invalidation, ack/retry).
 */
export type RecoveryRefreshHandler<TEnv extends OAuthEnvLike = OAuthEnvLike> = (
  token: unknown,
  env: TEnv,
  db: unknown,
  kv: unknown,
  message: unknown,
  metrics: RefreshMetrics
) => Promise<void>;

/**
 * Inline-refresh handler — called by `attemptInlineRefresh` ON the per-family
 * token-endpoint POST step ONLY. The surrounding code keeps the D1/KV loading
 * and encryption work; the per-family handler is just the HTTP call.
 *
 * R2#14: original spec's `(ctx, encryptedRefreshToken, connectingUserId)`
 * signature was wrong — those values are loaded inside attemptInlineRefresh,
 * not passed by the caller. Plaintext refresh token + env is sufficient.
 */
export type InlineRefreshHandler<TEnv extends OAuthEnvLike = OAuthEnvLike> = (
  plaintextRefreshToken: string,
  env: TEnv
) => Promise<InlineRefreshResult>;

/**
 * Common fields on every family lifecycle config.
 */
interface FamilyLifecycleBase {
  readonly name: OAuthFamilyName;

  /**
   * Access-token TTL in seconds — `"permanent"` for non-expiring tokens
   * (Shopify, Mailchimp).
   */
  readonly accessTokenTtlSec: number | "permanent";

  /**
   * Refresh-token TTL in seconds. `"permanent"` for non-rotating refresh
   * tokens; `"none"` for permanent-access providers without a refresh path.
   */
  readonly refreshTokenTtlSec: number | "permanent" | "none";

  /** True if the refresh token rotates on every refresh (Pinterest, MS Ads). */
  readonly refreshTokenRotates: boolean;

  /** True if the cron should proactively refresh before expiry. */
  readonly needsProactiveRefresh: boolean;

  /** Failure-counter thresholds (mirrors the legacy PROVIDER_CONFIG). */
  readonly warnOnConsecutiveFailures?: number;
  readonly criticalOnConsecutiveFailures?: number;

  /** Optional health-check endpoint (Mailchimp / TikTok / HubSpot today). */
  readonly healthCheckEndpoint?: string;
  readonly healthCheckAuthPrefix?: "Bearer" | "OAuth";

  /** Day-based thresholds for expiry detection (Meta / LinkedIn / TikTok). */
  readonly warnDaysBeforeExpiry?: number;
  readonly criticalDaysBeforeExpiry?: number;
  readonly autoRefreshDaysBefore?: number;

  /** Hour-based thresholds for short-lived tokens (TikTok 24h, HubSpot 30min). */
  readonly warningThresholdHours?: number;
  readonly criticalThresholdHours?: number;

  /** Refresh-token-clock warn threshold (TikTok 30d, LinkedIn 30d). */
  readonly refreshTokenWarnDays?: number;

  /** Env keys the dispatcher pre-flight checks before invoking the adapter. */
  readonly requiredEnvKeys?: ReadonlyArray<OAuthEnvKey>;

  /**
   * Declarative provider-side token revocation (Story 32.1 AC #9).
   *
   * When present, the grant-revoke route (apps/server/src/routes/
   * integrations/revoke-grant.ts) calls
   * `POST {url}?{tokenParam}={plaintextToken}` BEST-EFFORT after the local
   * rows are deleted — provider failure never rolls back the local revoke.
   * Families WITHOUT this field do a LOCAL-ONLY revoke (rows deleted; the UI
   * tells the user to revoke credentials in the provider's dashboard).
   *
   * This replaces any per-provider switch (oauth-integration directive,
   * Anti-Pattern 1): adding revocation support for a family is ONE field
   * here, never a new code path in the route.
   *
   * Google semantics (verified against the in-repo GOOGLE_REVOKE_URL
   * behavior in routes/integrations/disconnect.ts and Google OAuth2 docs,
   * 2026-06): revoking either token of a pair invalidates both; HTTP 200 =
   * revoked, HTTP 400 = already invalid (treated as success).
   */
  readonly revocationEndpoint?: {
    readonly url: string;
    readonly method: "POST";
    readonly tokenParam: string;
  };

  /**
   * Queue health-check handler. PR-1 ships with stubs that throw if invoked
   * (the dispatcher is not yet wired to call them); PR-2 swaps the dispatchers
   * AND replaces the stubs with real handlers in lockstep.
   */
  readonly healthCheck: HealthCheckHandler;

  /** Queue recovery-refresh handler. Same PR-1 stub policy as healthCheck. */
  readonly recoveryRefresh: RecoveryRefreshHandler;
}

/**
 * Discriminated union — when `inlineRefreshOn401: true`, `inlineRefreshHandler`
 * is REQUIRED at the type level (TD19). `tsc` rejects misconfiguration before
 * a 401 ever fires.
 */
export type FamilyLifecycleConfig =
  | (FamilyLifecycleBase & {
      readonly inlineRefreshOn401: false;
      readonly inlineRefreshHandler?: never;
    })
  | (FamilyLifecycleBase & {
      readonly inlineRefreshOn401: true;
      readonly inlineRefreshHandler: InlineRefreshHandler;
    });

// ============================================================================
// PR-1 stub handlers (Discovery Item 7)
// ============================================================================

const PR1_STUB_SENTINEL = "isPr1Stub" as const;

function tagAsStub<T extends (...args: never[]) => unknown>(fn: T, label: string): T {
  Object.defineProperty(fn, PR1_STUB_SENTINEL, {
    value: true,
    enumerable: false,
    writable: false,
    configurable: false,
  });
  Object.defineProperty(fn, "stubLabel", {
    value: label,
    enumerable: false,
    writable: false,
    configurable: false,
  });
  return fn;
}

function makePr1HealthCheckStub(label: string): HealthCheckHandler {
  return tagAsStub(
    async () => {
      throw new Error(
        `PR-1 stub: healthCheck for ${label} not yet wired. PR-2 must replace this before the dispatcher reads from OAUTH_FAMILIES.`
      );
    },
    `healthCheck:${label}`
  );
}

function makePr1RecoveryRefreshStub(label: string): RecoveryRefreshHandler {
  return tagAsStub(
    async () => {
      throw new Error(
        `PR-1 stub: recoveryRefresh for ${label} not yet wired. PR-2 must replace this before the dispatcher reads from OAUTH_FAMILIES.`
      );
    },
    `recoveryRefresh:${label}`
  );
}

function makePr1InlineRefreshStub(label: string): InlineRefreshHandler {
  return tagAsStub(
    async () => {
      throw new Error(
        `PR-1 stub: inlineRefreshHandler for ${label} not yet wired. PR-2 must replace this before api-call-tracker reads from OAUTH_FAMILIES.`
      );
    },
    `inlineRefreshHandler:${label}`
  );
}

/**
 * Predicate: was the given function created by one of the `makePr1*Stub`
 * factories and never replaced?
 *
 * The PR-2 runtime-gate test in oauth-families.test.ts uses this to assert
 * that no family in OAUTH_FAMILIES still carries a stub after the cutover.
 * Plain `typeof === "function"` cannot tell stub from real handler.
 */
export function isPr1StubHandler(fn: unknown): boolean {
  if (typeof fn !== "function") return false;
  return Reflect.get(fn, PR1_STUB_SENTINEL) === true;
}

// ============================================================================
// OAUTH_FAMILIES — the registry (TD9 frozen-identifier order)
// ============================================================================

/**
 * Canonical ordering: matches OAUTH_PROVIDER_VALUES exactly. Reordering is
 * forbidden (TD9) — rolling deploys may briefly mix old + new code, and name-
 * order drift would not silently drop messages, but the build-time test in
 * oauth-families.test.ts asserts the exact list anyway as a tripwire.
 *
 * TTLs sourced from PROVIDER_CONFIG (8 already-wired families) plus per-
 * provider OAuth files for Microsoft Ads + Pinterest Ads (Stories 19.3 / 19.4).
 *
 * Threshold coverage matches the frozen baseline at
 * apps/server/src/cron/__fixtures__/provider-config-baseline.json — the
 * snapshot test in apps/server (TD13) catches drift.
 */
export const OAUTH_FAMILIES = {
  google: {
    name: "google",
    accessTokenTtlSec: 3600,
    refreshTokenTtlSec: "permanent",
    refreshTokenRotates: false,
    needsProactiveRefresh: false,
    inlineRefreshOn401: true,
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: ["GOOGLE_CLIENT_ID", "GOOGLE_CLIENT_SECRET"],
    // Story 32.1 AC #9 — first (and currently only) family with declarative
    // provider-side revocation. 200 = revoked, 400 = already revoked = fine.
    revocationEndpoint: {
      url: "https://oauth2.googleapis.com/revoke",
      method: "POST",
      tokenParam: "token",
    },
    healthCheck: makePr1HealthCheckStub("google"),
    recoveryRefresh: makePr1RecoveryRefreshStub("google"),
    inlineRefreshHandler: makePr1InlineRefreshStub("google"),
  },
  meta: {
    name: "meta",
    accessTokenTtlSec: 5184000, // 60 days (long-lived)
    refreshTokenTtlSec: "none", // Meta refreshes via long-lived-token exchange
    refreshTokenRotates: false,
    needsProactiveRefresh: true,
    inlineRefreshOn401: false,
    warnDaysBeforeExpiry: 10,
    criticalDaysBeforeExpiry: 3,
    autoRefreshDaysBefore: 14,
    requiredEnvKeys: ["META_APP_ID", "META_APP_SECRET"],
    healthCheck: makePr1HealthCheckStub("meta"),
    recoveryRefresh: makePr1RecoveryRefreshStub("meta"),
  },
  linkedin: {
    name: "linkedin",
    accessTokenTtlSec: 5184000, // 60 days
    refreshTokenTtlSec: 31536000, // 365 days
    refreshTokenRotates: false,
    needsProactiveRefresh: true,
    inlineRefreshOn401: false,
    warnDaysBeforeExpiry: 10,
    criticalDaysBeforeExpiry: 3,
    autoRefreshDaysBefore: 14,
    refreshTokenWarnDays: 30,
    requiredEnvKeys: ["LINKEDIN_CLIENT_ID", "LINKEDIN_CLIENT_SECRET"],
    healthCheck: makePr1HealthCheckStub("linkedin"),
    recoveryRefresh: makePr1RecoveryRefreshStub("linkedin"),
  },
  tiktok: {
    name: "tiktok",
    accessTokenTtlSec: 86400, // 24h — most aggressive
    refreshTokenTtlSec: 31536000, // 365d
    refreshTokenRotates: false,
    needsProactiveRefresh: true,
    inlineRefreshOn401: false, // boundary: 24h not < 24h
    warningThresholdHours: 4,
    criticalThresholdHours: 1,
    autoRefreshDaysBefore: 1,
    refreshTokenWarnDays: 30,
    warnOnConsecutiveFailures: 2,
    criticalOnConsecutiveFailures: 3,
    healthCheckEndpoint:
      "https://open.tiktokapis.com/v2/user/info/?fields=open_id",
    healthCheckAuthPrefix: "Bearer",
    requiredEnvKeys: ["TIKTOK_CLIENT_KEY", "TIKTOK_CLIENT_SECRET"],
    healthCheck: makePr1HealthCheckStub("tiktok"),
    recoveryRefresh: makePr1RecoveryRefreshStub("tiktok"),
  },
  shopify: {
    name: "shopify",
    accessTokenTtlSec: "permanent",
    refreshTokenTtlSec: "none",
    refreshTokenRotates: false,
    needsProactiveRefresh: false,
    inlineRefreshOn401: false,
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    healthCheck: makePr1HealthCheckStub("shopify"),
    recoveryRefresh: makePr1RecoveryRefreshStub("shopify"),
  },
  mailchimp: {
    name: "mailchimp",
    accessTokenTtlSec: "permanent",
    refreshTokenTtlSec: "none",
    refreshTokenRotates: false,
    needsProactiveRefresh: false,
    inlineRefreshOn401: false,
    warnOnConsecutiveFailures: 2,
    criticalOnConsecutiveFailures: 3,
    healthCheckEndpoint: "https://login.mailchimp.com/oauth2/metadata",
    healthCheckAuthPrefix: "OAuth",
    healthCheck: makePr1HealthCheckStub("mailchimp"),
    recoveryRefresh: makePr1RecoveryRefreshStub("mailchimp"),
  },
  hubspot: {
    name: "hubspot",
    accessTokenTtlSec: 1800, // 30 min — shortest in system
    refreshTokenTtlSec: "permanent",
    refreshTokenRotates: false,
    needsProactiveRefresh: true,
    inlineRefreshOn401: true,
    warningThresholdHours: 0,
    criticalThresholdHours: 0,
    autoRefreshDaysBefore: 0,
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    healthCheckEndpoint: "https://api.hubapi.com/oauth/v3/introspect",
    healthCheckAuthPrefix: "Bearer",
    requiredEnvKeys: ["HUBSPOT_CLIENT_ID", "HUBSPOT_CLIENT_SECRET"],
    healthCheck: makePr1HealthCheckStub("hubspot"),
    recoveryRefresh: makePr1RecoveryRefreshStub("hubspot"),
    inlineRefreshHandler: makePr1InlineRefreshStub("hubspot"),
  },
  klaviyo: {
    name: "klaviyo",
    accessTokenTtlSec: 3600, // 1h
    refreshTokenTtlSec: "permanent",
    refreshTokenRotates: false,
    needsProactiveRefresh: true,
    inlineRefreshOn401: true,
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: ["KLAVIYO_CLIENT_ID", "KLAVIYO_CLIENT_SECRET"],
    healthCheck: makePr1HealthCheckStub("klaviyo"),
    recoveryRefresh: makePr1RecoveryRefreshStub("klaviyo"),
    inlineRefreshHandler: makePr1InlineRefreshStub("klaviyo"),
  },
  "microsoft-ads": {
    name: "microsoft-ads",
    accessTokenTtlSec: 3600, // 1h
    refreshTokenTtlSec: 7776000, // ~90d (Microsoft refresh-token rotation lifetime)
    refreshTokenRotates: true,
    needsProactiveRefresh: true,
    inlineRefreshOn401: true,
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: [
      "MICROSOFT_ADS_CLIENT_ID",
      "MICROSOFT_ADS_CLIENT_SECRET",
    ],
    healthCheck: makePr1HealthCheckStub("microsoft-ads"),
    recoveryRefresh: makePr1RecoveryRefreshStub("microsoft-ads"),
    inlineRefreshHandler: makePr1InlineRefreshStub("microsoft-ads"),
  },
  "pinterest-ads": {
    name: "pinterest-ads",
    accessTokenTtlSec: 2592000, // 30d
    refreshTokenTtlSec: 5184000, // 60d
    refreshTokenRotates: true,
    needsProactiveRefresh: true,
    inlineRefreshOn401: false, // 30d access — cron has plenty of buffer
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: [
      "PINTEREST_ADS_CLIENT_ID",
      "PINTEREST_ADS_CLIENT_SECRET",
    ],
    healthCheck: makePr1HealthCheckStub("pinterest-ads"),
    recoveryRefresh: makePr1RecoveryRefreshStub("pinterest-ads"),
  },
  "snapchat-ads": {
    name: "snapchat-ads",
    // Aggressive 60-min access token TTL — refresh required well before expiry.
    // inlineRefreshOn401: true is REQUIRED by TD4 invariant (access TTL < 24h).
    accessTokenTtlSec: 3600, // 1h
    // Snap docs do NOT specify a refresh-token TTL. Observed behaviour treats
    // refresh tokens as effectively permanent (matches Klaviyo). The adapter
    // defensively persists rotated refresh_token if Snap returns one.
    refreshTokenTtlSec: "permanent",
    refreshTokenRotates: false, // Snap doesn't document rotation; defensive persistence (TD20)
    needsProactiveRefresh: true,
    inlineRefreshOn401: true, // TD4: 1h < 24h → MUST be true
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: [
      "SNAPCHAT_ADS_CLIENT_ID",
      "SNAPCHAT_ADS_CLIENT_SECRET",
    ],
    healthCheck: makePr1HealthCheckStub("snapchat-ads"),
    recoveryRefresh: makePr1RecoveryRefreshStub("snapchat-ads"),
    inlineRefreshHandler: makePr1InlineRefreshStub("snapchat-ads"),
  },
  "reddit-ads": {
    name: "reddit-ads",
    // Reddit's `expires_in` is always 3600s. Same aggressive 1h TTL as Klaviyo /
    // MS Ads / Snapchat — TD4 mandates inlineRefreshOn401: true (access < 24h).
    accessTokenTtlSec: 3600, // 1h
    // Reddit treats `duration=permanent` refresh tokens as effectively permanent.
    // The refresh response NEVER includes a `refresh_token` field — the adapter
    // carries forward the existing refresh_token unchanged on every refresh.
    // This INVERTS the Snapchat 19.7 defensive-rotation pattern (Snap always
    // returns it; Reddit never does). Source: github.com/reddit-archive/reddit/wiki/OAuth2.
    refreshTokenTtlSec: "permanent",
    refreshTokenRotates: false, // Reddit confirmed non-rotating (verified)
    needsProactiveRefresh: true,
    inlineRefreshOn401: true, // TD4: 1h < 24h → MUST be true
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: ["REDDIT_ADS_CLIENT_ID", "REDDIT_ADS_CLIENT_SECRET"],
    healthCheck: makePr1HealthCheckStub("reddit-ads"),
    recoveryRefresh: makePr1RecoveryRefreshStub("reddit-ads"),
    inlineRefreshHandler: makePr1InlineRefreshStub("reddit-ads"),
  },
  "amazon-ads": {
    name: "amazon-ads",
    // LWA `expires_in` is always 3600s. Same aggressive 1h TTL as Klaviyo /
    // MS Ads / Snapchat / Reddit — TD4 mandates inlineRefreshOn401: true.
    accessTokenTtlSec: 3600, // 1h
    // LWA refresh tokens are valid indefinitely unless the user removes the
    // website/app from their authorized-apps list. The refresh response NEVER
    // includes a `refresh_token` field — the adapter carries forward the
    // existing refresh_token unchanged on every refresh. Mirrors Reddit 19.10
    // non-rotating pattern. Refresh tokens survive 180-day client-secret
    // rotation as long as `client_id` is unchanged.
    // Source: https://developer.amazon.com/docs/login-with-amazon/refresh-token.html
    refreshTokenTtlSec: "permanent",
    refreshTokenRotates: false, // LWA confirmed non-rotating (verified)
    needsProactiveRefresh: true,
    inlineRefreshOn401: true, // TD4: 1h < 24h → MUST be true
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: ["AMAZON_ADS_CLIENT_ID", "AMAZON_ADS_CLIENT_SECRET"],
    // PR-1 stubs are replaced by real handlers via runtime mutation in
    // `apps/server/src/queues/token-refresh.ts` (Task 5 wiring). The
    // `oauth-families.test.ts` assertion via `isPr1StubHandler` enforces that
    // the cutover has occurred for production builds.
    healthCheck: makePr1HealthCheckStub("amazon-ads"),
    recoveryRefresh: makePr1RecoveryRefreshStub("amazon-ads"),
    inlineRefreshHandler: makePr1InlineRefreshStub("amazon-ads"),
  },
  ahrefs: {
    // Story 20.1 — FIRST family in the codebase using a per-user-supplied API
    // key (Bearer) as the access token. The customer pastes their Ahrefs
    // Enterprise API key at connect time; we encrypt it with the same
    // encryptToken helper that OAuth tokens use, store it in oauth_tokens
    // under provider="ahrefs", and treat it as a 1-year-lived "access token"
    // for all lifecycle machinery (cron expiry detection, health-check, etc.)
    //
    // Why requiredEnvKeys: [] — Ahrefs has NO server-side OAuth client; every
    // customer brings their own key. There is no `AHREFS_CLIENT_ID`/`SECRET`
    // to configure on the worker. This is a deliberate first-of-its-kind
    // shape and a future agent should NOT add bogus env keys to "match" the
    // other families.
    //
    // Why recoveryRefresh is a permanent PR-1-style stub — there is NO
    // automated recovery path for an API key. The 1-year expiry is too long
    // to "refresh" (no machine-to-machine refresh exists for API keys), and a
    // 401 means the customer revoked or rotated the key in their Ahrefs
    // dashboard. The only remediation is a user-initiated re-paste via the
    // PUT /:integrationId/ahrefs/api-key route. The token-health monitor
    // surfaces a 14-day pre-expiry warning so the customer rotates before
    // service interruption.
    //
    // Why inlineRefreshOn401: false — same reason. There is no inline-refresh
    // handler we could plausibly wire; the discriminated union forbids
    // inlineRefreshHandler when inlineRefreshOn401: false, which is correct.
    //
    // Future Ahrefs Connect OAuth (Story 20.1a) will land as a SEPARATE
    // family entry "ahrefs-connect" — the API-key path stays untouched per
    // AC #16. Both will share the same adapter implementation because the
    // wire format is identical: Authorization: Bearer <key-or-token>.
    name: "ahrefs",
    accessTokenTtlSec: 31_536_000, // 1 year — Ahrefs API key default lifetime
    refreshTokenTtlSec: "none",
    refreshTokenRotates: false,
    needsProactiveRefresh: false, // 1-year is too long for cron-driven refresh
    inlineRefreshOn401: false, // 401 = customer must rotate; no auto-refresh
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: [], // FIRST family with no env keys (per-user secret model)
    healthCheck: makePr1HealthCheckStub("ahrefs"),
    recoveryRefresh: makePr1RecoveryRefreshStub("ahrefs"),
  },
  semrush: {
    // Story 20.4 — SECOND family in the codebase using a per-user-supplied
    // API key as the access token. Mirrors Ahrefs's API-key-Bearer scaffold
    // (Story 20.1) with FOUR deliberate divergences: (1) auth shape — `key=`
    // query parameter, NOT `Authorization: Bearer`; (2) response format —
    // semicolon-delimited CSV, NOT JSON; (3) unit-cost economics — per-row
    // for most reports + 40u fixed for backlinks_overview + 50u/row for
    // phrase_kdi; (4) 30-day ToS §3.3 data-persistence cap (see cache.ts
    // STALE_EXPIRATION_TTL_BY_PLATFORM["semrush"]).
    //
    // Why requiredEnvKeys: [] — Semrush has NO server-side OAuth client; every
    // customer brings their own key via dialog. There is no SEMRUSH_CLIENT_ID/
    // SECRET to configure on the worker. This is the SECOND family (after
    // Ahrefs 20.1) with the per-user-secret shape; a future agent should
    // NOT add bogus env keys to "match" the OAuth families.
    //
    // Why recoveryRefresh is a permanent PR-1-style stub — there is NO
    // automated recovery path for an API key. Per developer.semrush.com/api/
    // basics/faq/ verified 2026-05-08, "you can't rotate the key by
    // yourself" — rotation requires a Semrush support ticket. The only
    // remediation on a 401 is the customer rotating via Semrush support
    // and then re-pasting via the PUT /:integrationId/semrush/api-key route.
    //
    // Why inlineRefreshOn401: false — same reason. The discriminated union
    // forbids inlineRefreshHandler when inlineRefreshOn401: false, which is
    // correct — there is no inline-refresh handler we could plausibly wire.
    //
    // Why accessTokenTtlSec: "permanent" — Semrush API keys do NOT expire on
    // a published schedule (DIVERGENT from Ahrefs which expires keys at
    // exactly 1 year). Keeps the token-health monitor from emitting bogus
    // expiry warnings; mirrors Klaviyo's permanent-access-token model.
    //
    // 30-day Semrush ToS §3.3 data-persistence cap (verified 2026-05-08):
    //   "you agree not to cache the information received from the Services
    //    for more than one (1) month without the express written consent of
    //    Semrush." See packages/integrations/src/cache.ts STALE_EXPIRATION_
    //    TTL_BY_PLATFORM["semrush"] = 2_592_000 (30 days exactly).
    //
    // Future Semrush OAuth (Story 20.4d or later) — the Projects + Local
    // APIs support OAuth 2.0; Story 20.4 does NOT consume those endpoints.
    // The future story will land as a SEPARATE family entry "semrush-oauth"
    // with accessTokenTtlSec: 3600 (1h OAuth tokens) AND inlineRefreshOn401:
    // true. The API-key path stays untouched per AC #17. Both will share the
    // same adapter implementation because Semrush accepts the same `key=`
    // query parameter for OAuth access tokens too (per
    // developer.semrush.com/api/introduction/semrush-api-overview/).
    name: "semrush",
    accessTokenTtlSec: "permanent", // Semrush keys do not expire on a published schedule
    refreshTokenTtlSec: "none",
    refreshTokenRotates: false,
    needsProactiveRefresh: false,
    inlineRefreshOn401: false, // 401 = customer must rotate via Semrush support; no auto-refresh
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: [], // SECOND family with no env keys (per-user secret model)
    healthCheck: makePr1HealthCheckStub("semrush"),
    recoveryRefresh: makePr1RecoveryRefreshStub("semrush"),
  },
  moz: {
    // Story 20.7 — THIRD family in the codebase using a per-user-supplied
    // API token as the access credential. Mirrors Semrush's API-key scaffold
    // (Story 20.4) with FIVE deliberate divergences:
    //   1. Auth shape — `x-moz-token` custom HTTP header on every request
    //      (NOT `?key=` query param like Semrush, NOT `Authorization: Bearer`
    //      like Ahrefs). Verified moz.com/api/docs/guides/authentication
    //      2026-05-10.
    //   2. Wire format — JSON-RPC 2.0 envelope at a single universal endpoint
    //      (`https://api.moz.com/jsonrpc`); every method is a POST with
    //      `{ jsonrpc: "2.0", id, method, params: { data: {...} } }`.
    //   3. Async-call envelope — Brand Authority and other expensive methods
    //      return `result.async_call.id` immediately and require polling.
    //   4. Snapshot-only API — no historical-time-series surface in V3 (we
    //      persist our own monthly snapshots in `moz_authority_snapshots` per
    //      AC #16; this is a build-to-sell decision).
    //   5. Self-serve token rotation — UNLIKE Semrush (whose customers must
    //      contact support per developer.semrush.com/api/basics/faq/), Moz
    //      customers can generate up to 5 active tokens at any time and
    //      regenerate revoked ones via the Moz API dashboard
    //      (verified moz.com/api/docs/guides/authentication 2026-05-10).
    //
    // Why requiredEnvKeys: [] — Moz has NO server-side OAuth client; every
    // customer brings their own token via the MozApiKeyDialog. There is no
    // MOZ_CLIENT_ID/SECRET to configure on the worker. This is the THIRD
    // family (after Ahrefs 20.1 + Semrush 20.4) with the per-user-secret
    // shape; a future agent should NOT add bogus env keys to "match" the
    // OAuth families.
    //
    // Why recoveryRefresh is a permanent PR-1-style stub — there is NO
    // automated recovery path for an API token. The Moz token is permanent
    // (no published schedule expiry) and the only remediation on a 401 is
    // the customer rotating in the Moz API dashboard and re-pasting via the
    // PUT /:integrationId/moz/api-token route. UNLIKE Semrush this is
    // self-serve (no support ticket needed).
    //
    // Why inlineRefreshOn401: false — same reason. The discriminated union
    // forbids inlineRefreshHandler when inlineRefreshOn401: false, which is
    // correct — there is no inline-refresh handler we could plausibly wire.
    //
    // Why accessTokenTtlSec: "permanent" — Moz API tokens do NOT expire on a
    // published schedule (verified moz.com/api/docs/guides/authentication
    // 2026-05-10). They remain valid until the customer manually revokes
    // them in the Moz API dashboard. Mirrors Semrush 20.4 + Klaviyo's
    // permanent-access-token model. Keeps the token-health monitor from
    // emitting bogus expiry warnings.
    //
    // No 30-day data-persistence ToS cap (verified moz.com/legal/terms
    // 2026-05-10 — no equivalent to Semrush ToS §3.3 found). The 7-day
    // global stale fallback at packages/integrations/src/cache.ts:138 is
    // sufficient. NO entry needed in STALE_EXPIRATION_TTL_BY_PLATFORM.
    //
    // Future Moz keyword-data methods (`data.keyword.metrics.fetch.all`,
    // `data.keyword.searchIntent.fetch`, `data.site.ranking-keywords.list`)
    // — currently catalogued but NOT shipped in Story 20.7. They land
    // incrementally in Story 20.7a / 20.8 as additional helpers in
    // packages/integrations/src/moz/api.ts. The mozJsonRpcCall signature is
    // method-agnostic so adding new methods is purely additive (no family
    // entry change required).
    name: "moz",
    accessTokenTtlSec: "permanent", // Moz tokens do not expire on a published schedule
    refreshTokenTtlSec: "none",
    refreshTokenRotates: false,
    needsProactiveRefresh: false,
    inlineRefreshOn401: false, // 401 = customer self-rotates in Moz API dashboard; no auto-refresh
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: [], // THIRD family with no env keys (per-user secret model)
    healthCheck: makePr1HealthCheckStub("moz"),
    recoveryRefresh: makePr1RecoveryRefreshStub("moz"),
  },
  callrail: {
    // Story 22.1 — FOURTH family in the codebase using a per-user-supplied
    // API token as the access credential. Mirrors Ahrefs 20.1 / Semrush 20.4
    // / Moz 20.7's API-key scaffold with FOUR deliberate divergences:
    //   1. Auth shape — `Authorization: Token token="<API_KEY>"` HTTP
    //      header. CallRail uses a NON-STANDARD Authorization value: literally
    //      the word "Token" followed by a space, then `token="..."`. This
    //      is NOT `Authorization: Bearer <token>` (Ahrefs), NOT `?key=`
    //      query param (Semrush), and NOT `x-moz-token: <value>` header
    //      (Moz). Verified apidocs.callrail.com/ and
    //      support.callrail.com/hc/en-us/articles/5711821845389-CallRail-s-API
    //      2026-05-14.
    //   2. Wire format — REST + JSON (NOT JSON-RPC like Moz, NOT CSV like
    //      Semrush, NOT path-based REST like Ahrefs). Every CallRail v3
    //      call is a standard REST GET at account-scoped paths
    //      (`https://api.callrail.com/v3/a/{account_id}/calls.json` etc.);
    //      responses are plain JSON. Simplest of all API-key adapters.
    //   3. Required account discovery — CallRail keys are user-scoped
    //      within an organization and a single API key may have access to
    //      N accounts (multi-account orgs are common for resellers). The
    //      adapter MUST call `GET /v3/a.json` at connect time to discover
    //      accessible accounts and require the user to pick an account in
    //      a 2-step wizard connect dialog. The selected `account_id` is
    //      persisted as `integrations.account_id`. FIRST API-key dialog
    //      with 2-step state in our library.
    //   4. 25-month hard data-retention ceiling — per
    //      support.callrail.com/hc/en-us/articles/5711337552269-CallRail-s-data-retention-policy
    //      verified 2026-05-14: Communication Records are auto-deleted
    //      after 25 months; requesting earlier dates returns an error.
    //      The adapter pre-flight rejects `fetchMetrics` calls whose
    //      `dateRange.start < now - 25 months` BEFORE making the API call.
    //      FIRST adapter in our library with a hard-retention pre-flight.
    //
    // Why requiredEnvKeys: [] — CallRail has NO server-side OAuth client;
    // every customer brings their own token via the CallRailApiKeyDialog.
    // There is no CALLRAIL_CLIENT_ID/SECRET to configure on the worker.
    // This is the FOURTH family (after Ahrefs 20.1 + Semrush 20.4 +
    // Moz 20.7) with the per-user-secret shape; a future agent should NOT
    // add bogus env keys to "match" the OAuth families.
    //
    // Why recoveryRefresh is a permanent PR-1-style stub — there is NO
    // automated recovery path for an API token. The CallRail token is
    // permanent (no published schedule expiry) and the only remediation
    // on a 401 is the customer rotating in the CallRail API keys
    // dashboard and re-pasting via the PUT /:integrationId/callrail/api-token
    // route. UNLIKE Semrush this is **self-serve** like Moz —
    // app.callrail.com/account/api/keys allows any user with API
    // permission to generate/revoke tokens at will (verified
    // support.callrail.com/hc/en-us/articles/5711821845389-CallRail-s-API
    // 2026-05-14).
    //
    // Why inlineRefreshOn401: false — same reason. The discriminated
    // union forbids inlineRefreshHandler when inlineRefreshOn401: false,
    // which is correct — there is no inline-refresh handler we could
    // plausibly wire.
    //
    // Why accessTokenTtlSec: "permanent" — CallRail API tokens do NOT
    // expire on a published schedule. Per the CallRail API help center
    // verified 2026-05-14, "API keys do not expire. The only way to
    // remove a key is for the user to delete it from their account."
    // Mirrors Semrush 20.4 + Moz 20.7 + Klaviyo's permanent-access-token
    // model. Keeps the token-health monitor from emitting bogus expiry
    // warnings.
    //
    // No 30-day data-persistence ToS cap (verified
    // www.callrail.com/legal/terms-of-service 2026-05-14 has no
    // equivalent to Semrush ToS §3.3). The 7-day global stale fallback
    // at packages/integrations/src/cache.ts is sufficient. NO entry
    // needed in STALE_EXPIRATION_TTL_BY_PLATFORM.
    //
    // NEW for CallRail: 25-month hard-retention pre-flight at
    // fetchMetrics — adapter rejects synchronously with
    // AdapterErrorCode.validation_error if `dateRange.start` is past the
    // 25-month ceiling. See packages/integrations/src/callrail/retention.ts.
    // This is the FIRST adapter in our library with this pattern; future
    // Story 22.3 (CallTrackingMetrics) will likely need a similar guard
    // (CTM has its own retention policy).
    //
    // NO row-budget circuit-breaker (unlike Moz 20.7's 2K-rows/day,
    // Semrush 20.4's 200K-units/day, Ahrefs 20.1's 5M-units/day) —
    // CallRail does NOT charge per-row or per-unit; the only quota is
    // request-count (1,000/hr + 10,000/day general, 150/hr + 1,000/day
    // SMS, 100/hr + 2,000/day outbound, per
    // rollout.com/integration-guides/call-rail/api-essentials verified
    // 2026-05-14). The existing queue throttling layer handles this.
    //
    // SMS-send (150/hr + 1K/day) and outbound-call (100/hr + 2K/day)
    // quotas are SEPARATE and DO NOT apply to Story 22.1 — Story 22.1
    // consumes only READ endpoints (`/v3/a.json`, `/calls/*.json`,
    // `/form_submissions.json`). Future Story 22.x that adds SMS send or
    // outbound call features will need separate quota tracking against
    // `callrail:sms_quota:*` + `callrail:outbound_quota:*` KV keys.
    //
    // Future CallRail expansion endpoints — Story 22.2 ships demo
    // presets + Summit Legal Group fixture; Story 22.4 ships attribution
    // display; Story 22.5 ships recording links (tenant-scoped, signed
    // URLs that bypass the cache). Adding new endpoints is a matter of
    // adding new helpers in `callrail/api.ts` — no family entry change
    // required.
    name: "callrail",
    accessTokenTtlSec: "permanent", // CallRail tokens do not expire on a published schedule
    refreshTokenTtlSec: "none",
    refreshTokenRotates: false,
    needsProactiveRefresh: false,
    inlineRefreshOn401: false, // 401 = customer self-rotates in CallRail API keys dashboard; no auto-refresh
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: [], // FOURTH family with no env keys (per-user secret model)
    healthCheck: makePr1HealthCheckStub("callrail"),
    recoveryRefresh: makePr1RecoveryRefreshStub("callrail"),
  },
  calltrackingmetrics: {
    // Story 22.3 — FIFTH family in the codebase using a per-user-supplied
    // API credential as the access token, and the FIRST family in the
    // codebase to require TWO secrets in one credential bundle (Access
    // Key + Secret Key combined via HTTP Basic auth). Mirrors Ahrefs 20.1
    // / Semrush 20.4 / Moz 20.7 / CallRail 22.1's API-key scaffold with
    // FIVE deliberate divergences:
    //
    //   1. Auth shape — `Authorization: Basic
    //      <base64(ACCESS_KEY:SECRET_KEY)>` HTTP header. CTM is the FIRST
    //      adapter in our library to require TWO secrets in one
    //      credential bundle — Ahrefs/Semrush/Moz/CallRail all used a
    //      SINGLE token. Verified
    //      calltrackingmetrics.zendesk.com/hc/en-us/articles/6349290257805-API-Keys
    //      and github.com/calltracking/calltracking.github.io/blob/master/api_users_guide.md
    //      (`-u ${ACCESS_KEY}:${SECRET_KEY}` curl example) 2026-05-15.
    //
    //   2. Wire format — REST + JSON with mandatory `.json` path-suffix
    //      on every endpoint. Every CTM v1 call is a standard REST GET
    //      at account-scoped paths (`/api/v1/accounts/{id}/calls.json`
    //      etc.). DIFFERENT from CallRail's `/v3/a/{id}/` shape — CTM
    //      paths require both `/api/v1/` AND `.json` suffix.
    //
    //   3. Account scope split — Agency keys span sub-accounts (per
    //      Settings > Parent Settings > API Integration); Account keys
    //      lock to one (per Settings > Account Settings > API
    //      Integration). The adapter calls `GET /api/v1/accounts.json`
    //      at connect time to discover accessible accounts and infers
    //      `keyScope: "agency" | "account"` from
    //      `allowedAccountIds.length` for telemetry. Mirrors CallRail
    //      22.1's 2-step wizard with the addition of dual-secret input.
    //      Verified
    //      www.calltrackingmetrics.com/blog/product-o/announcements/multi-account-level-api-access/
    //      2026-05-15.
    //
    //   4. **per_page=150 hard ceiling** — CTM `/calls.json`, `/sms.json`,
    //      and `/forms.json` enforce a 150-row maximum per page;
    //      exceeding triggers `HTTP 400` with the literal error
    //      `"per_page limit is 150"`. DIFFERENT from CallRail 22.1's 250.
    //      Adapter clamps client-side BEFORE building the URL.
    //
    //   5. **EU region toggle** — `api.eu.calltrackingmetrics.com` is
    //      catalogue-claimed for GDPR data residency but NOT publicly
    //      confirmed by web search. Adapter treats EU as runtime-probed:
    //      at connect time, if the user toggles "EU region", the adapter
    //      probes the EU hostname and falls back to US with a
    //      `ctm_eu_region_probe_failed` warning if DNS fails. **FIRST
    //      adapter in our library with a region selector.**
    //
    // **CRITICAL — COLUMN-OVERLOAD STORAGE PATTERN (FIRST in our library):**
    // CTM stores BOTH secrets in `oauth_tokens` by overloading the column
    // semantics:
    //   - `access_token_encrypted = <encrypted ACCESS_KEY>`
    //   - `refresh_token_encrypted = <encrypted SECRET_KEY>`
    //     (despite the column name — there is NO refresh flow; the
    //      column is reused as a second-secret bucket per the
    //      "column overload" pattern documented in this comment block).
    //   - `access_token_expires_at = NULL` (CTM keys do not expire on
    //      a published schedule)
    //   - `refresh_token_expires_at = NULL` (no refresh-side expiry
    //      concept exists per the column-overload)
    //
    // A future agent reading the schema MUST NOT misinterpret these
    // columns as OAuth — they are reused as `ACCESS_KEY` +
    // `SECRET_KEY` slots for this family ONLY. See
    // packages/integrations/src/ctm/ctm.ts top-of-file JSDoc for the
    // full rationale.
    //
    // Why requiredEnvKeys: [] — CTM has NO server-side OAuth client;
    // every customer brings their own Access Key + Secret Key via the
    // CtmApiKeyDialog. There is no CTM_CLIENT_ID/SECRET to configure
    // on the worker. This is the FIFTH family (after Ahrefs 20.1 +
    // Semrush 20.4 + Moz 20.7 + CallRail 22.1) with the per-user-secret
    // shape; a future agent should NOT add bogus env keys to "match"
    // the OAuth families.
    //
    // Why recoveryRefresh is a permanent PR-1-style stub — there is NO
    // automated recovery path for an API-key pair. The CTM keys are
    // permanent (no published schedule expiry) and the only remediation
    // on a 401 is the customer rotating in the CTM dashboard and
    // re-pasting via the PUT /:integrationId/ctm/api-keys route. Like
    // CallRail 22.1 + Moz 20.7 this is **self-serve** — any user with
    // API permission can generate/revoke keys at will via
    // app.calltrackingmetrics.com/accounts/setup/integrations
    // (verified
    // calltrackingmetrics.zendesk.com/hc/en-us/articles/6349290257805-API-Keys
    // 2026-05-15).
    //
    // Why inlineRefreshOn401: false — same reason. The discriminated
    // union forbids inlineRefreshHandler when inlineRefreshOn401: false,
    // which is correct — there is no inline-refresh handler we could
    // plausibly wire.
    //
    // Why accessTokenTtlSec: "permanent" — CTM API keys do NOT expire
    // on a published schedule. Per the CTM API Keys article verified
    // 2026-05-15, keys remain valid until manually revoked in the CTM
    // dashboard. Mirrors CallRail 22.1 + Moz 20.7 + Semrush 20.4 +
    // Klaviyo's permanent-access-token model. Keeps the token-health
    // monitor from emitting bogus expiry warnings.
    //
    // No 30-day data-persistence ToS cap (verified
    // www.calltrackingmetrics.com/legal/data-protection-addendum-2025/
    // 2026-05-15 has no equivalent to Semrush ToS §3.3). The 7-day
    // global stale fallback at packages/integrations/src/cache.ts is
    // sufficient. NO entry needed in STALE_EXPIRATION_TTL_BY_PLATFORM.
    //
    // NO row-budget circuit-breaker (unlike Moz 20.7's 2K-rows/day,
    // Semrush 20.4's 200K-units/day, Ahrefs 20.1's 5M-units/day) — CTM
    // does NOT bill per-row; the only quota is request-count (~300
    // req/min per account soft cap per community reports, unpublished).
    // The existing queue rate-limit throttling layer handles this.
    //
    // **VoiceAI usage is billed per-minute SEPARATELY** ($0.12/min after
    // 250 free min per
    // www.calltrackingmetrics.com/features/lead-management/voiceai
    // verified 2026-05-15) and counts SEPARATELY from API request-count
    // quotas. Story 22.3 only consumes READ endpoints (`/accounts.json`,
    // `/calls.json`, `/forms.json`, `/sms.json`); future Story 22.x
    // VoiceAI work will need separate quota tracking against
    // `ctm:voiceai_quota:*` + `ctm:ai_texting_quota:*` KV keys.
    //
    // Future CTM expansion endpoints — Sale endpoint enrichment
    // (`/calls/{id}/sale.json`) → Story 22.5+; Chat conversations +
    // Agent/Live Calls (`/users` `live_calls[]`) + Touchpoints/Legs/
    // Transfers + Triggers/Voice Menus + VoiceAI agents + AI Texting +
    // Scorecards + LeadReactors → Stories 22.5+. Webhooks
    // (`call_start`, `call_end`, `call_updated`, `sms_received`,
    // `chat_created`, `form_submitted` — 6 event types catalogued) →
    // Story 22.6+. Adding new endpoints is purely additive (new
    // helpers in `ctm/api.ts` + new transformers + `MetricDefinition`
    // rows).
    name: "calltrackingmetrics",
    accessTokenTtlSec: "permanent", // CTM keys do not expire on a published schedule
    refreshTokenTtlSec: "none",
    refreshTokenRotates: false,
    needsProactiveRefresh: false,
    inlineRefreshOn401: false, // 401 = customer self-rotates in CTM dashboard; no auto-refresh
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: [], // FIFTH family with no env keys (per-user secret model — but FIRST with TWO secrets in one credential bundle via column overload)
    healthCheck: makePr1HealthCheckStub("calltrackingmetrics"),
    recoveryRefresh: makePr1RecoveryRefreshStub("calltrackingmetrics"),
  },
  trustpilot: {
    // Story 23.3 — SIXTH family in the codebase using per-user-supplied secrets
    // as access credentials, but the FIRST family with a finite-but-no-refresh-
    // token TTL AND the FIRST per-user-API-key family with REAL re-issue
    // recoveryRefresh. Trustpilot uses OAuth 2.0 `client_credentials` grant
    // where the API Key + API Secret pair (HTTP-Basic-encoded) exchanges for
    // a 100-hour Bearer access token; there is NO refresh token — when the
    // access token expires, the same client_credentials request must be
    // re-issued. Verified developers.trustpilot.com/grant-type-client-credentials/
    // 2026-05-16.
    //
    // **FIVE divergences from CallRail 22.1 / CTM 22.3:**
    //   1. **TWO secrets in connect dialog (API Key + API Secret + domain)** —
    //      FIRST 3-input API-key dialog. Customer pastes from Trustpilot
    //      Business dashboard. Persisted in `trustpilot:credentials:{id}` KV
    //      side-channel encrypted-at-rest (separate from `oauth_tokens` D1
    //      row which only holds the short-lived access token).
    //   2. **100-hour finite-TTL access token** — `expires_in: 359999` (~4.17d)
    //      per `client_credentials` grant. FIRST per-user-API-key family with
    //      a finite numeric TTL (Ahrefs/Semrush/Moz/CallRail/CTM all
    //      "permanent"). The cron's `needsProactiveRefresh: true` sweep
    //      proactively re-issues ~24h before expiry to prevent the next sync
    //      from 401'ing.
    //   3. **REAL recoveryRefresh handler** (NOT PR-1 stub) — the cron CAN
    //      auto-recover by reading the encrypted API Key + Secret from KV
    //      side-channel, decrypting, calling
    //      `exchangeTrustpilotClientCredentials(apiKey, apiSecret)`, and
    //      persisting the new access token. FIRST per-user-API-key family
    //      where the cron actually performs a token re-issue (Ahrefs/Semrush/
    //      Moz/CallRail/CTM all route to `mark_revoked_notify_user`).
    //   4. **NO data-retention pre-flight** (DIVERGENT from CallRail 22.1's
    //      25-month ceiling) — Trustpilot reviews persist for the lifetime of
    //      the business unit (verified legal.trustpilot.com/end-user-terms-
    //      and-conditions + business.trustpilot.com/terms 2026-05-16 have no
    //      retention ceiling). Adapter does NOT pre-flight reject historical
    //      date ranges.
    //   5. **Domain-to-BusinessUnitID discovery flow** — Step 1 of the
    //      connect dialog requires the customer to type their Trustpilot
    //      domain (e.g., `greenlifewellness.com`). The adapter resolves the
    //      domain to a Business Unit ID via
    //      `GET /v1/business-units/find?name={domain}` (API-key auth). 2-step
    //      wizard pattern carried forward from CallRail 22.1; auto-skips
    //      Step 2 when the domain resolves to exactly 1 BU.
    //
    // **THREE structural concerns:**
    //   - **Paid-tier gating** — `/v1/private/business-units/{id}/reviews`
    //     requires Trustpilot Plus or higher. The connect-time entitlement
    //     probe sets `privateApiEntitled` in KV metadata; subsequent
    //     `fetchMetrics` skips the private endpoint when false (saves 1
    //     request + prevents 403).
    //   - **Reviewer PII discipline** — `consumer.displayName`,
    //     `consumer.displayLocation`, review `title`/`text`,
    //     `companyReply.text` (always), `consumer.email`/`referralEmail`/
    //     `referenceId`/`tags` (paid tier). The `sanitizeTrustpilotPii` helper
    //     redacts these before any log/Sentry breadcrumb.
    //   - **`source` and `status` enum drift** — Trustpilot does NOT publish
    //     canonical enum values; adapter passes them through as raw strings
    //     AND emits a forensic `console.log` ONCE per fetch on unknown
    //     values (deduped via request-scoped Set).
    //
    // Why requiredEnvKeys: [] — Trustpilot has NO server-side OAuth client.
    // Every customer brings their own API Key + Secret via
    // `TrustpilotCredentialsDialog`. There is no TRUSTPILOT_CLIENT_ID/SECRET
    // to configure on the worker. This is the SIXTH family with the
    // per-user-secret shape; a future agent should NOT add bogus env keys.
    //
    // Why inlineRefreshOn401: false — access TTL is 100h > 24h (TD4
    // invariant: inline refresh ONLY when access TTL < 24h). The cron's
    // 15-min proactive sweep handles re-issue; a customer would never see an
    // expired Bearer token because the sweep beats expiry by ~24h. The
    // discriminated union forbids `inlineRefreshHandler` when
    // `inlineRefreshOn401: false`.
    //
    // No 30-day data-persistence ToS cap (verified
    // legal.trustpilot.com/end-user-terms-and-conditions +
    // business.trustpilot.com/terms 2026-05-16 — no equivalent to Semrush
    // ToS §3.3). NO entry needed in STALE_EXPIRATION_TTL_BY_PLATFORM.
    //
    // NO row-budget circuit-breaker — Trustpilot bills only by request count
    // (833/5min = ~150/min ceiling + 10K/hr per
    // developers.trustpilot.com/rate-limiting/ verified 2026-05-16). Existing
    // queue rate-limit throttling handles it.
    //
    // **Invitations API** (`invitations-api.trustpilot.com`) is OUT-OF-SCOPE
    // for Story 23.3 — Story 23.3 consumes only READ endpoints. Future
    // write-mutation story will need a separate base-URL constant and
    // separate rate-limit budget.
    name: "trustpilot",
    accessTokenTtlSec: 359999, // ~100 hours per client_credentials grant verified 2026-05-16
    refreshTokenTtlSec: "none", // client_credentials returns NO refresh token
    refreshTokenRotates: false,
    needsProactiveRefresh: true, // FIRST API-key family with proactive sweep (re-issue ~24h before expiry)
    inlineRefreshOn401: false, // TD4: access TTL 100h > 24h; cron proactive sweep handles it
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: [], // SIXTH family with no env keys (per-user secret model)
    healthCheck: makePr1HealthCheckStub("trustpilot"),
    recoveryRefresh: makePr1RecoveryRefreshStub("trustpilot"),
  },
  yelp: {
    // Story 23.5 — SEVENTH family in the codebase using per-user-supplied
    // secrets as access credentials. Yelp Fusion uses **pure Bearer API-Key
    // auth** — there is NO OAuth handshake, NO token exchange, NO refresh
    // token, NO expiry. The customer pastes ONE 128-character API Key from
    // the Yelp Developer dashboard (https://yelp.com/developers/v3/manage_app)
    // and every API call includes `Authorization: Bearer <YELP_API_KEY>`
    // directly. Verified docs.developer.yelp.com/docs/fusion-authentication
    // 2026-05-18.
    //
    // **SIX divergences from Trustpilot 23.3:**
    //   1. **ONE secret in connect dialog (API Key + business URL/alias/ID)** —
    //      single password-masked input (mirrors CallRail 22.1's single-API-key
    //      shape, NOT Trustpilot's 3-input bundle). No client_credentials dance.
    //   2. **PERMANENT access-token TTL** — Yelp API Keys have no documented
    //      expiry; they remain valid until the customer rotates them in the
    //      Yelp Developer dashboard. `accessTokenTtlSec: "permanent"`
    //      (DIVERGENT from Trustpilot's 100h finite TTL).
    //   3. **PR-1 STUB recoveryRefresh** (NOT a REAL handler) — no automatic
    //      recovery is possible because Yelp ships ONE secret with no
    //      rotation-recovery mechanism. When the customer rotates the key,
    //      the only remediation is to re-paste the new key in the Integration
    //      Picker (DIVERGENT from Trustpilot which re-issues via stored
    //      client_credentials). A user-facing notification surfaces:
    //      "Yelp API Key was rotated — re-paste your API Key in the Integration
    //      Picker."
    //   4. **NO proactive sweep** — `needsProactiveRefresh: false` because Yelp
    //      API Keys are permanent (no expiry to proactively refresh against).
    //      The cron's 15-min sweep ONLY performs health-checks for Yelp; it
    //      NEVER attempts a refresh action (DIVERGENT from Trustpilot which
    //      has `needsProactiveRefresh: true`).
    //   5. **NEW D1 table `yelp_rating_snapshots`** — Yelp Fusion has NO
    //      historical API; the adapter must snapshot `rating` + `review_count`
    //      daily to build any trend line. SECOND adapter with snapshot
    //      persistence (after Moz 20.7's `moz_authority_snapshots`).
    //   6. **NEW per-tenant daily-call-budget circuit-breaker** at
    //      `yelp:budget:{tenantId}:{YYYY-MM-DD}` KV key. FIRST adapter with a
    //      per-tenant per-day CALL budget (250/day ceiling on 300/day Starter;
    //      leaves headroom for 96 health-checks/day + 24-48 fetches/day).
    //      Ahrefs 20.1 has per-tenant per-day UNIT budgets, Semrush 20.4 has
    //      per-tenant per-month UNIT budgets, Moz 20.7 has per-tenant per-day
    //      ROW budgets — Yelp is the FIRST per-tenant per-day CALL budget.
    //
    // **THREE structural concerns:**
    //   - **Paid-tier gating for reviews endpoint** — Yelp's Fusion API has a
    //     paid-only model since 2024 (Starter $7.99/1000 has NO reviews
    //     access; Plus $9.99/1000 unlocks 3 most-recent excerpts; Enterprise
    //     $14.99/1000 unlocks 7 most-recent excerpts). The connect-time probe
    //     calls `GET /v3/businesses/{id}/reviews?limit=1` to detect tier
    //     entitlement; `reviewsEndpointEntitled` flag persists in KV metadata.
    //   - **Reviewer PII discipline** — `user.name`, `user.id`,
    //     `user.profile_url`, `user.image_url`, review `text`, `phone`,
    //     `display_phone`. The `sanitizeYelpPii` helper redacts these before
    //     any log/Sentry breadcrumb.
    //   - **3-7 most-recent-excerpts architectural cap** — Yelp returns ONLY
    //     the 3 (Enhanced) or 7 (Premium) most recent reviews with ~160-char
    //     text excerpts. Full review history is architecturally inaccessible
    //     (NOT a retention cap — there is no "deeper" endpoint). Document as a
    //     product constraint, not a retention guard.
    //
    // Why requiredEnvKeys: [] — Yelp has NO server-side OAuth client. Every
    // customer brings their own API Key. There is no YELP_CLIENT_ID/SECRET to
    // configure on the worker. SEVENTH family with the per-user-secret shape.
    //
    // Why inlineRefreshOn401: false — there is no refresh to inline; the key
    // is either valid or rotated. On 401, the integration is marked
    // `refresh_failed` and the user is notified to re-paste.
    //
    // No data-persistence ToS cap (verified docs.developer.yelp.com/docs/
    // fusion-terms 2026-05-18 — no equivalent to Semrush ToS §3.3). NO entry
    // needed in STALE_EXPIRATION_TTL_BY_PLATFORM; Yelp uses the global 7-day
    // stale fallback.
    //
    // No reply-state surface — Yelp Fusion's `/businesses/{id}/reviews`
    // response shape lacks `owner_response` / `companyReply` fields, so the
    // adapter ships NO reply-latency / reply-rate derived metrics (DIVERGENT
    // from Google Reviews 23.1 + Trustpilot 23.3). Owner replies must be
    // posted manually via Yelp for Business — Yelp Fusion is read-only.
    //
    // **Health-check budget cost:** Cron runs every 15 min × 24 hours × N
    // integrations = 96 health-checks/day per integration — burns 96 of the
    // 300/day Starter daily-call budget. This is intentional; leaves 204
    // calls/day for fetchMetrics (1-2 syncs/hr per integration = ~24-48/day).
    // Customer with multiple Yelp integrations on a shared Starter key CAN
    // exhaust budget — recommend Plus (500/day) or Enterprise (5000/day).
    name: "yelp",
    accessTokenTtlSec: "permanent", // Yelp API Keys never expire (customer-controlled rotation)
    refreshTokenTtlSec: "none",
    refreshTokenRotates: false,
    needsProactiveRefresh: false, // no expiry to refresh against
    inlineRefreshOn401: false, // FALSE per TD4 — no refresh handler to inline
    warnOnConsecutiveFailures: 3,
    criticalOnConsecutiveFailures: 5,
    requiredEnvKeys: [], // SEVENTH family with no env keys (per-user secret model)
    healthCheck: makePr1HealthCheckStub("yelp"),
    recoveryRefresh: makePr1RecoveryRefreshStub("yelp"), // PR-1 STUB — no auto-recovery possible (DIVERGENT from Trustpilot 23.3 REAL handler)
  },
} as const satisfies Record<OAuthFamilyName, FamilyLifecycleConfig>;

// ============================================================================
// resolveOAuthFamily — TD6 strict resolver
// ============================================================================

/**
 * Map a Platform to its OAuth family. Throws loudly if the platform has no
 * family declared in ADAPTER_META — TD6 chose strict-throw over `?? platform`
 * fallback because typos and missing-family-declarations should be loud, not
 * silent.
 *
 * PageSpeed (api-key) bypasses the call paths that invoke this helper via the
 * `oauthTokenId === null` short-circuit in api-call-tracker; it should never
 * reach this function.
 *
 * Used by widget-populate.ts (PR-2 fixes the hardcoded "google" bug at
 * widget-populate.ts:547) and by the runtime tests in this package.
 */
export function resolveOAuthFamily(platform: Platform): OAuthFamilyName {
  const family = ADAPTER_META[platform]?.oauthFamily;
  if (!family) {
    throw new Error(
      `resolveOAuthFamily: platform "${platform}" has no oauthFamily declared in ADAPTER_META — ` +
        `if it's an OAuth integration, declare oauthFamily; if it's api-key, this function should not be called for it. ` +
        `See packages/integrations/src/registry.ts and Task 1.4b in the token-lifecycle-refactor spec.`
    );
  }
  return family as OAuthFamilyName;
}

// Type re-exports for downstream consumers
export type { AdapterRefreshContext as RefreshContext };
export type { OAuthCredentials, RefreshedTokens, AdapterError };
